import React, { useState, useEffect } from "react";
import {
  Receipt,
  Download,
  ExternalLink,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
} from "lucide-react";
import { API_BASE_URL } from "../../config/api";
import { openExternalUrl } from "../../utils/subscriptionHandler";

const BillingSettings = ({ darkMode, user }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchInvoices = async (cursor = null) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/get-invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          limit: 10,
          startingAfter: cursor,
        }),
      });

      if (!response.ok) throw new Error("Failed to fetch invoices");

      const data = await response.json();

      if (cursor) {
        setInvoices((prev) => [...prev, ...data.invoices]);
      } else {
        setInvoices(data.invoices);
      }
      setHasMore(data.hasMore);
      setNextCursor(data.nextCursor);
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4 text-[#3d7a5a]" />;
      case "open":
        return <Clock className="w-4 h-4 text-[#c5a34e]" />;
      case "void":
        return <XCircle className="w-4 h-4 text-[#7ab892]" />;
      case "uncollectible":
        return <AlertCircle className="w-4 h-4 text-[#c45a2d]" />;
      case "draft":
        return <FileText className="w-4 h-4 text-[#a5d4b8]" />;
      default:
        return <Receipt className="w-4 h-4 text-[#a5d4b8]" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return "Paid";
      case "open":
        return "Pending";
      case "void":
        return "Void";
      case "uncollectible":
        return "Failed";
      case "draft":
        return "Draft";
      default:
        return status;
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "paid":
        return darkMode
          ? "bg-[#3d7a5a]/30 text-[#3d7a5a] border-[#3d7a5a]"
          : "bg-green-100 text-green-700 border-green-300";
      case "open":
        return darkMode
          ? "bg-[#c5a34e]/30 text-[#c5a34e] border-[#c5a34e]"
          : "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "void":
        return darkMode
          ? "bg-[#152a1c] text-[#a5d4b8] border-[#2a5038]"
          : "bg-gray-100 text-gray-600 border-gray-300";
      case "uncollectible":
        return darkMode
          ? "bg-[#c45a2d]/30 text-[#c45a2d] border-[#c45a2d]"
          : "bg-red-100 text-red-700 border-red-300";
      default:
        return darkMode
          ? "bg-[#152a1c] text-[#a5d4b8] border-[#2a5038]"
          : "bg-gray-100 text-gray-600 border-gray-300";
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4 text-forest-100">
          Billing History
        </h3>

        {/* Error Message */}
        {error && (
          <div
            className={`p-4 rounded-lg flex items-center gap-3 mb-4 ${
              darkMode
                ? "bg-[#c45a2d]/30 border border-[#c45a2d]"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <AlertCircle className="w-5 h-5 text-ember-500" />
            <p className="text-sm text-forest-100">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {invoices.length === 0 && !error && (
          <div className="p-8 rounded-lg text-center bg-forest-700">
            <Receipt className="w-12 h-12 mx-auto mb-3 text-forest-300" />
            <p className="text-sm text-forest-200">
              No invoices yet. Invoices will appear here after you subscribe.
            </p>
          </div>
        )}

        {/* Invoice List */}
        {invoices.length > 0 && (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className={`p-4 rounded-lg border ${
                  darkMode
                    ? "bg-[#1e3a28] border-[#2a5038]"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-forest-100">
                        {formatAmount(invoice.amount, invoice.currency)}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusStyles(
                          invoice.status,
                        )}`}
                      >
                        {getStatusIcon(invoice.status)}
                        {getStatusLabel(invoice.status)}
                      </span>
                    </div>
                    <div className="text-sm text-forest-200">
                      <span>{formatDate(invoice.created)}</span>
                      {invoice.number && (
                        <span className="ml-2">#{invoice.number}</span>
                      )}
                    </div>
                    {invoice.description && (
                      <p className="text-xs mt-1 text-forest-300">
                        {invoice.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {invoice.pdfUrl && (
                      <button
                        onClick={() => openExternalUrl(invoice.pdfUrl)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "hover:bg-[#2a5038] text-[#a5d4b8] hover:text-[#d0eadb]"
                            : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                        }`}
                        title="Download PDF"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    )}
                    {invoice.hostedUrl && (
                      <button
                        onClick={() => openExternalUrl(invoice.hostedUrl)}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? "hover:bg-[#2a5038] text-[#a5d4b8] hover:text-[#d0eadb]"
                            : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                        }`}
                        title="View Invoice"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <button
                onClick={() => fetchInvoices(nextCursor)}
                disabled={loadingMore}
                className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${
                  darkMode
                    ? "bg-[#1e3a28] hover:bg-[#2a5038] text-[#d0eadb]"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                }`}
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div
        className={`p-4 rounded-lg ${
          darkMode
            ? "bg-[#c5a34e]/20 border border-[#c5a34e]"
            : "bg-blue-50 border border-blue-200"
        }`}
      >
        <p className="text-sm text-forest-100">
          Invoices are generated automatically when your subscription renews.
          You can download PDF receipts for your records.
        </p>
      </div>
    </div>
  );
};

export default BillingSettings;
